import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { BASE_URI, prettyPrintDate } from '../Config';
import DownloadButton from './DownloadButton';
import { MhcAttentionJobMeta, MhcAttentionResultRow } from './types';

function formatModelType(modelType: string): string {
    return modelType === 'hlae' ? 'MhcAttention HLA-E' : 'MhcAttention';
}

function hasMultipleAlleles(alleles: string | null | undefined): boolean {
    if (!alleles) {
        return false;
    }

    return alleles
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean).length > 1;
}

function formatMetric(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }

    if (typeof value === 'number') {
        return Math.abs(value) >= 0.01 || value === 0 ? value.toFixed(6).replace(/\.?0+$/, '') : value.toExponential(4);
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return value;
    }

    return formatMetric(parsed);
}

const Job: React.FC = () => {
    const [resultRows, setResultRows] = useState<MhcAttentionResultRow[]>([]);
    const [jobMeta, setJobMeta] = useState<MhcAttentionJobMeta | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { userToken } = useAuth();
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        if (!userToken || !id) {
            setIsLoading(false);
            return;
        }

        let ignore = false;
        let intervalId: number | null = null;

        const fetchJob = async (initialLoad: boolean) => {
            if (initialLoad) {
                setIsLoading(true);
            }

            try {
                const response = await fetch(`${BASE_URI}/mhcattention/jobs/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    }
                });

                const data = await response.json();

                if (!ignore && response.ok) {
                    if (data.job_meta) {
                        setJobMeta(data.job_meta);
                        if (data.job_meta.status !== 'Pending' && intervalId !== null) {
                            window.clearInterval(intervalId);
                            intervalId = null;
                        }
                    }

                    if (Array.isArray(data.result_rows)) {
                        setResultRows(data.result_rows);
                    } else {
                        setResultRows([]);
                    }
                }
            } catch (error) {
                if (!ignore) {
                    console.error('There was an error retrieving the MHC Attention job:', error);
                }
            } finally {
                if (!ignore && initialLoad) {
                    setIsLoading(false);
                }
            }
        };

        intervalId = window.setInterval(() => {
            void fetchJob(false);
        }, 10000);

        void fetchJob(true);

        return () => {
            ignore = true;
            if (intervalId !== null) {
                window.clearInterval(intervalId);
            }
        };
    }, [id, userToken]);

    if (!userToken) {
        return <Navigate to="/signin" replace />;
    }

    const showBestAllele = resultRows.some((row) => hasMultipleAlleles(row.alleles));

    return (
        <div id="content-pane">
            {isLoading ? (
                <div className="spinner"></div>
            ) : (
                <>
                    {jobMeta && (
                        <div className="job_info">
                            <h3>Job information</h3>
                            <div className="info_item">
                                <span className="info_field">File name:</span>
                                <span>{jobMeta.filename}</span>
                            </div>
                            <div className="info_item">
                                <span className="info_field">Created:</span>
                                <span>{prettyPrintDate(jobMeta.created_at)}</span>
                            </div>
                            <div className="info_item">
                                <span className="info_field">Model:</span>
                                <span>{formatModelType(jobMeta.model_type)}</span>
                            </div>
                            <div className="info_item">
                                <span className="info_field">Status:</span>
                                <span>{jobMeta.status}</span>
                            </div>
                            <div className="info_item">
                                <span className="info_field">Input file:</span>
                                <span>
                                    <DownloadButton
                                        url_str={`mhcattention/jobs/${jobMeta.id}/download/input`}
                                        filename={`input_${jobMeta.filename}`}
                                        text="Download"
                                    />
                                </span>
                            </div>
                            {jobMeta.status === 'Succeeded' && (
                                <div className="info_item">
                                    <span className="info_field">Output file:</span>
                                    <span>
                                        <DownloadButton
                                            url_str={`mhcattention/jobs/${jobMeta.id}/download/output`}
                                            filename={`output_${jobMeta.filename}`}
                                            text="Download"
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    {jobMeta?.status === 'Pending' && (
                        <p className="job_status_note">
                            This job is still running. This page refreshes automatically while it is pending.
                        </p>
                    )}
                    {jobMeta?.failure_reason && (
                        <div className="job_failure_info">
                            <p>{jobMeta.failure_reason}</p>
                            <p>
                                For input formatting, supported alleles, and output interpretation, see the <Link to="/tutorial">Tutorial</Link>.
                            </p>
                        </div>
                    )}
                    {resultRows.length > 0 && (
                        <div>
                            <h3>Results</h3>
                            <div className="table_shell">
                                <table className="score_data">
                                    <thead>
                                        <tr>
                                            <th>Peptide</th>
                                            <th>Alleles</th>
                                            {showBestAllele && <th>Best allele</th>}
                                            <th>Score</th>
                                            <th>Percentile</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resultRows.map((row, index) => (
                                            <tr key={`${row.peptide}-${index}`}>
                                                <td>{row.peptide}</td>
                                                <td>{row.alleles}</td>
                                                {showBestAllele && <td>{row.mhcattention_best_allele || 'N/A'}</td>}
                                                <td>{formatMetric(row.mhcattention_score)}</td>
                                                <td>{formatMetric(row.mhcattention_pct)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {jobMeta?.status === 'Succeeded' && resultRows.length === 0 && (
                        <p className="job_status_note">
                            The output file is available for download even though no preview rows were returned.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default Job;
